import type { ChatRequest } from './hooks/useChatStream';
import type { ParentPart } from './types';

const STORAGE_KEY = 'home-pending-chat-request';
const MAX_AGE_MS = 2 * 60 * 1000;

export type HomeChatHandoffResult = {
  request: ChatRequest;
  documentIdsByUploadKey: Map<string, string>;
  fallbackUrlsByUploadKey: Map<string, string>;
};

export type HomeChatHandoff = {
  pendingParts: ParentPart[];
  resolveRequest: () => Promise<HomeChatHandoffResult>;
};

let deferredHandoff: HomeChatHandoff | null = null;

type HeldRequest = {
  request: ChatRequest;
  stashedAt: number;
};

let held: HeldRequest | null = null;

function isFresh(stashedAt: number): boolean {
  return Date.now() - stashedAt <= MAX_AGE_MS;
}

function isChatRequest(value: unknown): value is ChatRequest {
  return !!value && typeof value === 'object' && 'parts' in value && Array.isArray(value.parts);
}

function readStored(): ChatRequest | null {
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !('request' in parsed) ||
      !('stashedAt' in parsed) ||
      typeof parsed.stashedAt !== 'number' ||
      !isChatRequest(parsed.request)
    ) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (!isFresh(parsed.stashedAt)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    held = { request: parsed.request, stashedAt: parsed.stashedAt };
    return parsed.request;
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/** Hands a composer submission from the refresh homepage to the chat page. */
export function stashHomeChatRequest(request: ChatRequest): void {
  const stashedAt = Date.now();
  held = { request, stashedAt };
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ request, stashedAt }));
}

/** Home send, or `?q=` on /chat — used to paint the conversation before the stream starts. */
export function peekHomeChatHandoff(): ChatRequest | null {
  const stored = peekHomeChatRequest();
  if (stored) return stored;
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search).get('q')?.trim();
  if (!q) return null;
  return { parts: [{ type: 'text', text: q }] };
}

export function peekHomeChatRequest(): ChatRequest | null {
  if (held) {
    if (!isFresh(held.stashedAt)) {
      consumeHomeChatRequest();
      return null;
    }
    return held.request;
  }
  return readStored();
}

export function consumeHomeChatRequest(): void {
  held = null;
  window.sessionStorage.removeItem(STORAGE_KEY);
}

/** In-flight homepage upload: navigate immediately, finish the request on /chat. */
export function stashHomeChatHandoff(handoff: HomeChatHandoff): void {
  deferredHandoff = handoff;
}

export function peekHomeChatHandoffDeferred(): HomeChatHandoff | null {
  return deferredHandoff;
}

/** Clears the singleton only if it is still `expected`. Omit `expected` to drop any handoff. */
export function consumeHomeChatHandoff(expected?: HomeChatHandoff): void {
  if (expected !== undefined && deferredHandoff !== expected) return;
  deferredHandoff = null;
}
